// ── SHARING WORK INTO A CONVERSATION ─────────────────────────────────────────
// The budget for a share card, in one place because two very different files
// need the same numbers and neither owns them: `utils/imageCompress` encodes
// the thumbnail to them, and `firebase/chatService` enforces them on the way
// out — mirroring what firestore.rules enforces on the way in.
//
// THREE KINDS, ONE SHAPE. A project, a published photo and a published 3D
// design differ only in where the card opens; the message field, the composer,
// the rules and the renderer are identical for all three. So `kind` is a
// discriminator rather than three parallel implementations that would drift.

import { ITEM_KINDS } from './gallery';

export const SHARE_KINDS = { PROJECT: 'project', PHOTO: 'photo', DESIGN: 'design' };

/**
 * A gallery item as the `share` field a message carries.
 *
 * The two vocabularies meet HERE and nowhere else — every caller that turns a
 * tile into a share goes through this, so the mapping cannot be got right in
 * one place and wrong in another.
 *
 * `refId` is the ITEM's id, not the project behind a design: the card opens the
 * owner's profile at that item (`?photo=` / `?design=`, the deep links the
 * profile already understands), which shows the 3D scene AND the thread that
 * belongs to it. The project's own /share page carries neither.
 *
 * A tile has no title of its own in older records, hence the fall back to the
 * description — the tile itself is labelled the same way.
 */
export function shareFromGalleryItem(item, kind) {
  const itemKind = item?.kind || kind;
  return {
    kind: itemKind === ITEM_KINDS.DESIGN ? SHARE_KINDS.DESIGN : SHARE_KINDS.PHOTO,
    ownerUid: item?.ownerUid || '',
    refId: item?.id || '',
    title: item?.title || item?.description || '',
  };
}

export const SHARE_TITLE_MAX = 120;

// The picture rides ON the message, so every reader of the thread downloads it
// on every load — the same argument as the inline image preview in
// chatService, one size down again because a card thumbnail is drawn at about
// 72 CSS px, not at 560.
export const SHARE_THUMB_MAX_PX = 240;
export const SHARE_THUMB_QUALITY = 0.62;

// Measured on the STORED string, which is what Firestore counts and what the
// rules can check — roughly 22KB decoded. The ceiling belongs here and in the
// rules rather than only in the encoder: the sender chooses what to encode, so
// a limit that lives only on their machine is not a limit.
export const SHARE_THUMB_MAX_CHARS = 30000;
